import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import "./CSS/App.css";
import "./CSS/menuGrid.css";
import "./CSS/EnvVars.css";
import "./CSS/InputForm.css";
import "./CSS/FeaturedProducts.css";
import "./CSS/FixedViewport.css"; 
import { getThemeValue, getCompanyName } from './utils/subdomainUtils';

import InputForm from "./components/InputForm";
import MenuGrid from "./components/MenuGrid";
import MenuCategoryGrid from "./components/MenuCategoryGrid";
import Loader from "./components/Loader";
import ProductOverlay from "./components/ProductOverlay";
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Cart from "./components/Cart";
import StartScreen from "./components/StartScreen";
import LoginScreen from "./components/LoginScreen";
import AdminControls from "./components/AdminControls";

function App() {
  const [preferences, setPreferences] = useState("");
  const [selectedPreferences, setSelectedPreferences] = useState("");
  // AI Search
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  // AI Recommendations
  const [menuOptions, setMenuOptions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  // Sidebar catergory
  const [activeCategory, setActiveCategory] = useState('ai-recommendations');
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  // Full Menu
  const [fullMenu, setFullMenu] = useState(null);
  // Filtered Menu Items
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  // Start Screen
  const [showStartScreen, setShowStartScreen] = useState(true);
  // Menu Images
  const [menuImages, setMenuImages] = useState({});
  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'user' or 'admin'

  useEffect(() => {
    // Apply theme colors - map to all CSS variable names used throughout the app
    const primaryColor = getThemeValue('primaryColor');
    const secondaryColor = getThemeValue('secondaryColor');
    const backgroundColor = getThemeValue('backgroundColor');
    const textColor = getThemeValue('textColor');
    
    // Set all the different variable names used across CSS files
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    document.documentElement.style.setProperty('--background-color', backgroundColor);
    document.documentElement.style.setProperty('--text-color', textColor);
    
    // Map to existing CSS variable names
    document.documentElement.style.setProperty('--title-color', primaryColor);
    document.documentElement.style.setProperty('--title-gradient', `linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #f1f3f5 100%)`);
    document.documentElement.style.setProperty('--view-menu-btn-color', primaryColor);
    document.documentElement.style.setProperty('--view-menu-btn-hover-color', secondaryColor);
    document.documentElement.style.setProperty('--view-menu-btn-shadow', `0px 4px 10px ${primaryColor}30`);
    document.documentElement.style.setProperty('--loader-color', primaryColor);
    document.documentElement.style.setProperty('--loader-border-color', secondaryColor);
    
    // Dynamic color variables with opacity variations
    document.documentElement.style.setProperty('--primary-light', `${primaryColor}1a`); // 10% opacity
    document.documentElement.style.setProperty('--primary-medium', `${primaryColor}33`); // 20% opacity
    document.documentElement.style.setProperty('--primary-dark', secondaryColor);
    document.documentElement.style.setProperty('--background-preset', `${primaryColor}05`); // 2% opacity
    document.documentElement.style.setProperty('--shadow-focus', `0 0 0 3px ${primaryColor}1a`); // 10% opacity
    
    // Override hardcoded coral colors throughout the app
    // Convert hex to RGB for better opacity control
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const primaryRgb = hexToRgb(primaryColor);
    const secondaryRgb = hexToRgb(secondaryColor);
    
    if (primaryRgb && secondaryRgb) {
      // Set RGB format colors used throughout CSS files
      document.documentElement.style.setProperty('--coral-228-83-81', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      document.documentElement.style.setProperty('--coral-198-63-60', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
      
      // Override all hardcoded coral colors by adding a style tag
      const existingStyle = document.getElementById('dynamic-theme-override');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      const styleTag = document.createElement('style');
      styleTag.id = 'dynamic-theme-override';
      styleTag.innerHTML = `
        /* Override all hardcoded coral colors with dynamic theme colors */
        
        /* CSS class overrides */
        .input-title,
        .product-title,
        .cart-total,
        .size-option.selected,
        .add-on-option.selected,
        .featured-price,
        .size-price,
        .add-on-price,
        .price,
        .nutrition-card h3,
        .macro-label,
        .reason-header span {
          color: ${primaryColor} !important;
        }
        
        .view-cart-btn,
        .add-to-cart-btn,
        .modern-search-button,
        .size-option.selected,
        .add-on-option.selected {
          background-color: ${primaryColor} !important;
        }
        
        .size-option.selected,
        .add-on-option.selected {
          color: white !important;
        }
        
        .size-option.selected .size-price,
        .add-on-option.selected .add-on-price {
          color: white !important;
        }
        
        .view-cart-btn:hover,
        .add-to-cart-btn:hover,
        .modern-search-button:hover,
        .size-option:hover,
        .add-on-option:hover {
          background-color: ${secondaryColor} !important;
        }
      `;
      document.head.appendChild(styleTag);
    }

    // Fetch the full menu and images when the app loads
    fetchFullMenu();
    fetchMenuImages();
  }, []);

  const fetchFullMenu = () => {
    setLoading(true);
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://44.203.160.191:3000";
    const url = `${baseUrl}/getMenu`;
    console.log('Fetching menu from:', url);
    
    fetch(url, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        company_name: getCompanyName()
      })
    })
      .then(async res => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`HTTP error! status: ${res.status}, message: ${errorText}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Menu data received:', data);
        // Set the menu_items array from the response
        setFullMenu(data.menu_items || []);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching menu:", error);
        console.error("Error details:", {
          message: error.message,
          stack: error.stack
        });
        setFullMenu([]);
        setLoading(false);
      });
  };

  const fetchMenuImages = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://44.203.160.191:3000";
    const companyName = getCompanyName();
    const url = `${baseUrl}/getImages/${companyName}`;
    console.log('Fetching menu images from:', url);
    
    fetch(url, {
      method: "GET",
      headers: { 
        "Accept": "application/json"
      }
    })
      .then(async res => {
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`HTTP error! status: ${res.status}, message: ${errorText}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Menu images received:', data);
        setMenuImages(data);
      })
      .catch(error => {
        console.error("Error fetching menu images:", error);
        console.error("Error details:", {
          message: error.message,
          stack: error.stack
        });
        setMenuImages({});
      });
  };

  const handleSearch = () => {
    setSearched(true);
    setLoading(true);
    setMenuOptions([]);

    fetch(`${process.env.REACT_APP_BACKEND_URL || "http://44.203.160.191:3000"}/recommend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
         preference: selectedPreferences + " " + preferences,
         company_name: getCompanyName()
        })
    })
      .then(res => res.json())
      .then(data => {
        setMenuOptions(data);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching recommendations:", error);
        setLoading(false);
      });
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowCart(false);
  };

  const handleCloseOverlay = () => {
    setSelectedItem(null);
  };

  const handleAddToCart = (item) => {
    setCart([...cart, item]);
    setCartTotal(prevTotal => prevTotal + item.totalPrice);
    setSelectedItem(null); // Close the product overlay
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedCart = [...cart];
    updatedCart[index].quantity = newQuantity;
    setCart(updatedCart);
    
    // Recalculate total
    const newTotal = updatedCart.reduce((total, item) => {
      const itemTotal = (item.base_price + (item.selectedSize?.price || 0)) * item.quantity;
      const addOnsTotal = item.selectedAddOns?.reduce((sum, addOn) => sum + addOn.price, 0) || 0;
      return total + itemTotal + (addOnsTotal * item.quantity);
    }, 0);
    
    setCartTotal(newTotal);
  };

  const handleRemoveItem = (index) => {
    const updatedCart = cart.filter((_, i) => i !== index);
    setCart(updatedCart);
    
    // Recalculate total
    const newTotal = updatedCart.reduce((total, item) => {
      const itemTotal = (item.base_price + (item.selectedSize?.price || 0)) * item.quantity;
      const addOnsTotal = item.selectedAddOns?.reduce((sum, addOn) => sum + addOn.price, 0) || 0;
      return total + itemTotal + (addOnsTotal * item.quantity);
    }, 0);
    
    setCartTotal(newTotal);
  };

  const handleCheckout = () => {
    // TODO: Implement checkout functionality
    console.log('Proceeding to checkout with items:', cart);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category !== 'ai-recommendations' && fullMenu) {
      // Filter the cached menu items by category field
      const categoryItems = fullMenu.filter(item => item.category === category);
      setFilteredMenuItems(categoryItems);
    }
  };

  const calculateTotalQuantity = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleStartOrder = () => {
    setShowStartScreen(false);
  };

  const handleLogin = (role) => {
    setUserRole(role);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    setShowStartScreen(true);
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // Show admin controls if user is admin
  if (userRole === 'admin') {
    return <AdminControls onLogout={handleLogout} />;
  }

  // Show regular app for normal users
  return (
    <div className="viewport-wrapper">
      <div className="fixed-viewport-container">
        <div className="pos-container">
      {showStartScreen && (
        <StartScreen onStart={handleStartOrder} />
      )}
      
      <Navbar 
        cartTotal={cartTotal} 
        onViewCart={() => setShowCart(true)}
      />
      
      <div className="pos-content">
        <Sidebar 
          activeCategory={activeCategory} 
          onCategoryChange={handleCategoryChange}
          fullMenu={fullMenu}
        />
        
        <div className="menu-container">
          {activeCategory === 'ai-recommendations' ? (
            <>
              <motion.div
                className="thought-bubble"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 100, damping: 15 }}
              >
                {!searched ? (
                  <InputForm 
                    preferences={preferences} 
                    setPreferences={setPreferences} 
                    onSearch={handleSearch} 
                    setSelected={setSelectedPreferences}
                  />
                ) : loading ? (
                  <Loader />
                ) : (
                  <div className="f">
                    <div className="backButton" onClick={() => setSearched(false)}>
                      ← Back To Search
                    </div>
                    <MenuGrid 
                      options={menuOptions} 
                      onItemSelect={handleItemSelect}
                      menuImages={menuImages}
                    />
                  </div>
                )}
              </motion.div>
              {/* {!searched && !loading && (
                <FeaturedProducts 
                  onItemSelect={handleItemSelect}
                  menuImages={menuImages}
                />
              )} */}
            </>
          ) : loading ? (
            <Loader />
          ) : (
            <MenuCategoryGrid 
              items={filteredMenuItems} 
              onItemSelect={handleItemSelect}
              menuImages={menuImages}
              categoryName={activeCategory}
            />
          )}
        </div>
      </div>

      {selectedItem && (
        <ProductOverlay
          item={selectedItem}
          onClose={handleCloseOverlay}
          onAddToCart={handleAddToCart}
          menuImages={menuImages}
        />
      )}

      {showCart && (
        <Cart
          cart={cart}
          onClose={() => setShowCart(false)}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveItem={handleRemoveItem}
          onCheckout={handleCheckout}
        />
      )}

      {cart.length > 0 && !showCart && (
        <button 
          className="floating-checkout-btn"
          onClick={() => setShowCart(true)}
        >
          <span>View Cart</span>
          <span className="cart-count">{calculateTotalQuantity()}</span>
        </button>
      )}
        </div>
      </div>
    </div>
  );
}

export default App;
